import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import BookingModal from "./BookingModal";
import PodcastGuestModal from "./PodcastGuestModal";
import DiscoveryCallModal from "./DiscoveryCallModal";
import LocationModal from "./LocationModal";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "The Studio", id: "studio" },
  { label: "Packages", id: "packages" },
  { label: "Spotlight", id: "spotlight" },
  { label: "Contact", id: "contact" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPreselect, setBookingPreselect] = useState("");
  const [guestOpen, setGuestOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  // Subtle "Just booked · Nm ago" micro-toast rotation
  const [toastVisible, setToastVisible] = useState(false);
  const [toastKey, setToastKey] = useState(0);
  const [toastAge, setToastAge] = useState(8);
  useEffect(() => {
    const ages = [3, 8, 14, 22, 41, 6, 19];
    let i = 0;
    const showToast = () => {
      setToastAge(ages[i % ages.length]);
      setToastKey((k) => k + 1);
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 4500);
      i += 1;
    };
    // First fire 6s after mount, then every ~45s
    const first = setTimeout(showToast, 6000);
    const interval = setInterval(showToast, 45000);
    return () => {
      clearTimeout(first);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setBookingPreselect((e.detail && e.detail.package) || "");
      setBookingOpen(true);
    };
    window.addEventListener("sb:open-booking", handler);
    const guestHandler = () => setGuestOpen(true);
    window.addEventListener("sb:open-guest", guestHandler);
    const discoveryHandler = () => setDiscoveryOpen(true);
    window.addEventListener("sb:open-discovery", discoveryHandler);
    const locationHandler = () => setLocationOpen(true);
    window.addEventListener("sb:open-location", locationHandler);
    return () => {
      window.removeEventListener("sb:open-booking", handler);
      window.removeEventListener("sb:open-guest", guestHandler);
      window.removeEventListener("sb:open-discovery", discoveryHandler);
      window.removeEventListener("sb:open-location", locationHandler);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      // Active section detection
      const offsets = LINKS.map((l) => {
        const el = document.getElementById(l.id);
        if (!el) return { id: l.id, top: Infinity };
        const r = el.getBoundingClientRect();
        return { id: l.id, top: Math.abs(r.top - 120) };
      });
      offsets.sort((a, b) => a.top - b.top);
      setActive(offsets[0].id);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <motion.header
      data-testid="main-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#05050a]/75 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-[#00f0ff]/15 via-[#6b21d9]/20 to-[#ff0055]/15 border border-white/10 flex items-center justify-center overflow-hidden">
            <img
              src="/brand/sb-logo.svg"
              alt="SB Media Studio"
              className="w-8 h-8 object-contain"
              style={{ filter: "invert(1) brightness(1.15)" }}
            />
            <span className="absolute -inset-1 rounded-xl bg-gradient-to-br from-[#00f0ff] to-[#ff0055] opacity-0 blur-md group-hover:opacity-50 transition-opacity -z-10" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-[15px] tracking-tight flex items-center gap-2">
              <span>SB <span className="text-[#00f0ff]">Media</span></span>
              <span
                aria-hidden="true"
                data-testid="nav-waveform"
                className="flex items-end gap-[2px] h-3"
              >
                {[0.5, 0.85, 0.35, 0.7, 0.55].map((h, i) => (
                  <span
                    key={i}
                    className="wav-anim-bar inline-block w-[2px] rounded-full bg-[#00f0ff]"
                    style={{
                      height: `${h * 100}%`,
                      transformOrigin: "bottom center",
                      "--wav-dur": `${(0.9 + i * 0.11).toFixed(2)}s`,
                      "--wav-delay": `${(i * 0.07).toFixed(2)}s`,
                    }}
                  />
                ))}
              </span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.32em] text-[#a395c3]">
              Studio
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1 px-2 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.06]">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className={`relative px-4 py-2 text-[13px] font-medium rounded-full transition-colors ${
                active === l.id
                  ? "text-[#05050a]"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {active === l.id && (
                <motion.span
                  layoutId="nav-pill"
                  className="absolute inset-0 bg-[#00f0ff] rounded-full"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span className="relative">{l.label}</span>
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 relative">
          <a
            href="#discovery"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("sb:open-discovery"));
            }}
            data-testid="nav-cta-discovery"
            className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-transparent border border-[#00f0ff]/40 text-[#00f0ff] font-semibold text-[13px] hover:bg-[#00f0ff]/10 hover:border-[#00f0ff] transition-colors"
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-[#22c55e] opacity-70 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-[#22c55e]" />
            </span>
            Book a call
          </a>
          <a
            href="#book"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("sb:open-booking"));
            }}
            data-testid="nav-cta-book"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#05050a] font-semibold text-[13px] hover:bg-[#00f0ff] transition-colors"
          >
            Book Session
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff0055] animate-pulse" />
          </a>

          {/* Subtle "Just booked" micro-toast (desktop only) */}
          <AnimatePresence>
            {toastVisible && (
              <motion.div
                key={toastKey}
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.95 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                data-testid="nav-just-booked-toast"
                className="absolute top-[calc(100%+10px)] right-0 pointer-events-none flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0a0510]/95 border border-[#22c55e]/30 shadow-[0_8px_24px_-8px_rgba(34,197,94,0.35)] backdrop-blur"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-[#22c55e] opacity-70 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#22c55e]" />
                </span>
                <span className="text-[11px] uppercase tracking-[0.22em] text-white/90 whitespace-nowrap">
                  Just booked · {toastAge}m ago
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/10"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/5 bg-[#05050a]/95 backdrop-blur-xl"
          >
            <div className="px-5 py-6 space-y-1">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 rounded-xl text-white/80 hover:bg-white/5 hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  window.dispatchEvent(new CustomEvent("sb:open-booking"));
                }}
                data-testid="nav-mobile-cta-book"
                className="mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold"
              >
                Book Session
              </a>
              <a
                href="#discovery"
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  window.dispatchEvent(new CustomEvent("sb:open-discovery"));
                }}
                data-testid="nav-mobile-cta-discovery"
                className="mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-transparent border border-[#00f0ff]/40 text-[#00f0ff] font-semibold"
              >
                Book a call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
    <BookingModal
      open={bookingOpen}
      onClose={() => setBookingOpen(false)}
      preselectPackage={bookingPreselect}
    />
    <PodcastGuestModal open={guestOpen} onClose={() => setGuestOpen(false)} />
    <DiscoveryCallModal
      open={discoveryOpen}
      onClose={() => setDiscoveryOpen(false)}
    />
    <LocationModal
      open={locationOpen}
      onClose={() => setLocationOpen(false)}
    />
    </>
  );
};

export default Nav;
